/**
 * Vitally Analytics Integration
 *
 * Subscribes to Slzr.AnalyticsQueue to receive analytics events
 * and forwards them to Vitally for product analytics tracking.
 *
 * User identification and internal user filtering are handled
 * server-side in VitallyAnalyticsHelper.
 *
 * Depends on Slzr.AnalyticsQueue (from analytics-queue.js) being initialized
 * before initVitallyAnalytics is called. The load order and DOM-ready
 * initialization are managed by init.js.
 */

/**
 * Initialize the Vitally Analytics module
 *
 * Sets up Slzr.VitallyAnalytics with queue subscription
 * (matching the Google Analytics pattern).
 *
 * Requires Slzr.AnalyticsQueue to be initialized before this is called.
 * Calls Slzr.VitallyAnalytics.init() after setting up the object.
 *
 * @param {Object} Slzr - The Slzr global object
 */
export const initVitallyAnalytics = (Slzr) => {
  Slzr.VitallyAnalytics = {
    initialized: false,

    init() {
      if (this.initialized) return;
      if (typeof window.Vitally === 'undefined') return;

      this.initialized = true;
      this.subscribeToQueue();
    },

    subscribeToQueue() {
      if (!Slzr.AnalyticsQueue?.subscribe) return;

      Slzr.AnalyticsQueue.subscribe((event) => {
        if (
          event.providers?.length &&
          event.providers.includes(Slzr.AnalyticsQueue._providers.vitally)
        ) {
          this.trackEvent(event);
        }
      });
    },

    trackEvent(event) {
      const eventName = this.formatEventName(event.category, event.action);

      try {
        window.Vitally?.track({
          event: eventName,
          properties: {
            category: event.category,
            action: event.action,
            label: event.label,
            value: event.value,
            platform: event.platform
          }
        });
      } catch (err) {
        console.error('[Vitally] Error tracking event:', err);
      }
    },

    formatEventName(category, action) {
      const name = `${category || ''}-${action || ''}`;
      return name.toLowerCase().replace(/\s+/g, '-');
    }
  };
  Slzr.VitallyAnalytics.init();
};
